import { GetAppOutlined } from "@mui/icons-material";
import {
  Box,
  CardMedia,
  CircularProgress,
  Skeleton,
  Typography,
} from "@mui/material";
import React, { useRef, useState } from "react";
import TrashIcon from "../../../../Custom-imeges/TrashIcon";
import { VideoHistoryData } from "../../../../Types/VideoSuite-Types";
import DeleteModal from "../../../main-page/components/get-conversation/DeletePopup";
import { useDeleteVideoSuiteHistoryById } from "../../../../store/Apis/SuitesApps/VideoSuiteHistoryApi";
import { useQueryClient } from "@tanstack/react-query";
import { DeleteAllHistoryModal } from "../../../../Layouts/SuiteHistoryLayout/Shared/DeleteAllHistoryModal";
import { convertUtcToLocalDate } from "../../../../store/one-query/useMethodHelper";
import { getVideoSuiteHistoryAppIcon } from "./VideoSuiteHistoryDetailsModal";
import { ShareIcon } from "../../../../Custom-imeges/ShareIcon";
import { VideoDetailsView } from "../../Components/Shared/VideoSuiteHelper";

interface HandleEvent extends React.MouseEvent<HTMLDivElement> {}

interface VideoSuiteCardProps {
  videoData: VideoHistoryData;
  handleOpenVideoHistoryDetailsModal: (videoId: string) => void;
  isDeleteVideoHistoryLoading: boolean;
  DeleteVideoHistory: (itemId: String) => void;
  showVideoOptions?: { delete: boolean; download: boolean; share: boolean };
  cardView?: number;
}

const VideoSuiteHistoryCard = ({
  videoData,
  handleOpenVideoHistoryDetailsModal,
  isDeleteVideoHistoryLoading,
  DeleteVideoHistory,
  showVideoOptions = { delete: true, download: true, share: false },
  cardView,
}: VideoSuiteCardProps) => {
  const queryClient = useQueryClient();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoHover, setVideoHover] = useState(false);
  const [openDeleteVideoModal, setOpenDeleteVideoModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { mutate: deleteVideo } = useDeleteVideoSuiteHistoryById();

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch((error) => {
          console.error("Error playing video:", error);
        });
    }
    setVideoHover(true);
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
    setVideoHover(false);
  };

  // when video is loaded
  const handleVideoLoaded = () => {
    setIsLoading(false);
  };

  // when video fails to load
  const handleVideoError = () => {
    setIsLoading(false);
    console.error("Error loading video");
  };

  const handleDownload = (e: HandleEvent): void => {
    e.stopPropagation();

    if (!videoData.output) {
      console.error("Video URL is not available.");
      return;
    }
    const downloadLink = document.getElementById(
      "download-link"
    ) as HTMLAnchorElement;
    if (downloadLink) {
      downloadLink.href = videoData.output;
      downloadLink.download = "video.mp4"; // Set the desired file name
      downloadLink.click();
    }
  };

  const handleOpenDeleteModal = (e: HandleEvent) => {
    e.stopPropagation();
    setOpenDeleteVideoModal(true);
  };
  const handleDelete = () => {
    DeleteVideoHistory(videoData.id);
  };
  const handleOpenModal = () => {
    if (isLoading) return;
    handleOpenVideoHistoryDetailsModal(videoData.id);
  };

  const handleShareVideo = (e: HandleEvent) => {
    e.stopPropagation();
  }
  return (
    <Box
      className={`video-card ${
        videoHover && !isLoading ? "video-card-hover" : "video-card-not-hover"
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleOpenModal}
      sx={{
        cursor: isLoading ? "not-allowed" : "pointer",
      }}
    >
      {isLoading && (
        <Skeleton
          className="skeleton-background"
          variant="rectangular"
          width="100%"
          height="100%"
          animation="wave"
        />
      )}
      <video
        ref={videoRef}
        className={`video-element ${isLoading && "loading"}`}
        onLoadedData={handleVideoLoaded}
        onError={handleVideoError}
        loop
        muted
      >
        <source src={videoData.output} type="video/mp4" />
      </video>
      {videoHover && !isLoading && (
        <Box className="video-hover-overlay">
          <Box className="video-hover-content">
            <Box className="video-hover-actions">
              {showVideoOptions.download && (
                <Box className={`icon-container ${cardView === VideoDetailsView.library && "display-bottom"}`} onClick={handleDownload}>
                  <CardMedia
                    component={"img"}
                    image="/Images/VideoSuite/downloadIcon.svg"
                    className="icon-download-image"
                  />
                  <a id="download-link" style={{ display: "none" }} />
                </Box>
              )}
              {showVideoOptions.delete && (
                <Box className="icon-container" onClick={handleOpenDeleteModal}>
                  <TrashIcon className="trash-icon" />
                </Box>
              )}

              {showVideoOptions.share && (
                <Box className="icon-container share" onClick={handleShareVideo}>
                  <ShareIcon className="trash-icon" />
                </Box>
              )}
            </Box>
            <Box className="video-hover-footer">
              <Box className="video-footer-left">
                <Box className="circle-icon">
                  {getVideoSuiteHistoryAppIcon(videoData.appType)}
                </Box>
                <Typography
                  className="shared-typography "
                  sx={{
                    color: "#fff",
                  }}
                >
                  {videoData.appType}
                </Typography>
              </Box>
              {
              cardView !== VideoDetailsView.library &&
              <Typography
                className="shared-typography "
                sx={{
                  color: "#fff",
                }}
              >
                {convertUtcToLocalDate(videoData.createdAt)}
              </Typography>
              }
            
            </Box>
          </Box>
        </Box>
      )}
      {openDeleteVideoModal && (
        <Box onClick={(e) => e.stopPropagation()}>
          <DeleteAllHistoryModal
            canShow={openDeleteVideoModal}
            handleClose={() => setOpenDeleteVideoModal(false)}
            handleDeleteAllHistory={handleDelete}
            isDeleteLoading={isDeleteVideoHistoryLoading}
            message="Are you sure to delete this video?"
          />
        </Box>
      )}
    </Box>
  );
};

export default VideoSuiteHistoryCard;
